import { getConfig } from '../configs/game-config-provider'
import { disableAd, initBannerAd } from '../ad-manager'
import { setTrackingCommonData } from '../tracking'
import { CxfEvents, AdBannerEvent, AllowedBannerEvents, bannerTypeArray, bannerType } from '../constants'
import log from '../utils/logger'
import { getGameController } from './game-provider'
import { CXF } from '@goodgamestudios/cxf-runtime'
import { bannerConditionType, BannersConfigWithDefault } from '../configs/game-configs'
import { initJsLogic } from '../rules'
import { aggregateLoginData, AdBannersLoginData } from './events'

let cxf: CXF
let gameController: ReturnType<typeof getGameController>
let adConfig: BannersConfigWithDefault
let enabledBanners: bannerTypeArray = []
let disabledBanners: bannerTypeArray = []
let hasBanners: boolean | null = null

const validate = initJsLogic()

export type UpdatePaymentEventData = object

const updatePayHandler = (payload: UpdatePaymentEventData) => {
  evaluateBannerConditions(payload, 'paymentData')

  disableAd(disabledBanners)
}

const CUSTOM_HANDLERS_MAP = new Map<AllowedBannerEvents, typeof updatePayHandler>([
  [AdBannerEvent.UPDATE_PAY, updatePayHandler],
])

const setBackgroundImage = (backgroundImage?: URL) => {
  // if no image is set, fallback to white background
  if (!backgroundImage) {
    document.body.style.backgroundColor = '#fff'
    return
  }
  document.body.style.backgroundImage = `url(${backgroundImage})`
}

const init = (context: CXF) => {
  cxf = context
  gameController = getGameController(cxf)
  adConfig = getConfig(cxf)

  log('gameController: %O', gameController)
  registerHandlers()
  setBackgroundImage(adConfig.backgroundImage)
}

const addLoginHandler = async () => {
  aggregateLoginData(cxf, handleCxfLogin)
}

// Everytime a login event is triggered, we need to check if the user should see the banner ads or not
const handleCxfLogin = (loginData: AdBannersLoginData) => {
  setTrackingCommonData()
  if (loginData.isTempServer) {
    handleTempServerLogin()
  } else {
    handleNormalServerLogin(loginData)
  }
}

/**
 * When logging in temp server we cannot rely on the data from the login event
 * because it's not the same as the normal server, and at this point a normal login already happened
 * so just need to check the state of the banners and enable/disable it accordingly
 */
const handleTempServerLogin = () => {
  log('Temp server login detected!')
  if (hasBanners === null) {
    log('Error: first load logging in temp server!')
    return
  }

  if (hasBanners) {
    initBannerAd(adConfig, enabledBanners)
  }

  if (disabledBanners.length > 0) {
    disableAd(disabledBanners)
  }
}

const handleNormalServerLogin = (loginData: AdBannersLoginData) => {
  const userData = {
    ...loginData,
    originUrl: new URL(location.href).hostname,
  }

  evaluateBannerConditions(userData, 'loginData')

  if (hasBanners) {
    initBannerAd(adConfig, enabledBanners)
  }

  if (disabledBanners.length > 0) {
    disableAd(disabledBanners)
  }

  emitAdBannerStatusEvent(hasBanners as boolean, enabledBanners, disabledBanners)
}

const evaluateBannerConditions = (userData: object, conditionType: bannerConditionType) => {
  enabledBanners = []
  disabledBanners = []

  for (const [bannerType, { rules }] of Object.entries(adConfig.banners)) {
    if (validate(userData, rules[conditionType])) {
      enabledBanners.push(bannerType as bannerType)
    } else {
      log('The %s banner does not comply with %s rules: %O', bannerType, conditionType, userData)
      disabledBanners.push(bannerType as bannerType)
    }
  }

  hasBanners = enabledBanners.length > 0
}

const gameEventTrigger = (eventName: AllowedBannerEvents, payload: UpdatePaymentEventData) => {
  log(`triggerCallback - [${eventName}, ${payload}]`)
  if (!CUSTOM_HANDLERS_MAP.has(eventName)) {
    log(`Event ${eventName} is not registered in handlers map`)
  }

  const callback = CUSTOM_HANDLERS_MAP.get(eventName)!
  callback(payload)
}

export type GameEventTriggerType = typeof gameEventTrigger

const registerHandlers = () => {
  addLoginHandler()

  gameController.registerCustomGameHandlers(cxf, gameEventTrigger)
}

const emitAdBannerStatusEvent = (
  areBannersAvailable: boolean,
  enabledBanners: bannerTypeArray,
  disabledBanners: bannerTypeArray
) => {
  // Waiting to make sure IGS is ready to receive the event
  setTimeout(() => {
    cxf.emit(CxfEvents.AdBannerStatus, {
      areBannersAvailable,
      bannersDetails: {
        enabledBanners,
        disabledBanners,
      },
    })
  }, 5000)
}

export { init }
